import type { Breakpoint } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import React from 'react';
import Button from '../../components/Buttons/Button';
import { useResponsive } from '../../hooks/useResponsive';
import { useMessageService } from '@/shared/services/MessageService/MessageServiceContext';

const ActionModal: React.FC = () => {
  const { isMobile } = useResponsive();
  const { modalConfig, closeMessage } = useMessageService();

  if (!modalConfig || modalConfig.modalType !== 'action') return null;

  const {
    header,
    customBody,
    confirmLabel,
    cancelLabel,
    onConfirm,
    onCancel,
    size = 'medium',
    maxWidth,
    fullScreen = false,
    confirmButtonColor,
  } = modalConfig;

  const maxWidthMap: Record<'small' | 'medium' | 'large' | 'xlarge', Breakpoint> = {
    small: 'xs',
    medium: 'sm',
    large: 'md',
    xlarge: 'lg',
  };

  const handleConfirm = async () => {
    const result = await onConfirm?.();
    // Only close if onConfirm returns true or doesn't return false
    if (result !== false) {
      closeMessage();
    }
  };

  const handleCancel = () => {
    onCancel?.();
    closeMessage();
  };

  const paperClass = `${fullScreen ? 'fullscreen' : ''} ${isMobile && !fullScreen ? 'mobile' : ''}`.trim();

  return (
    <Dialog
      className="action-modal"
      open
      onClose={closeMessage}
      fullScreen={fullScreen}
      maxWidth={fullScreen ? false : maxWidth ? false : maxWidthMap[size]}
      fullWidth={!maxWidth && !fullScreen}
      slotProps={{
        paper: {
          className: paperClass,
          sx:
            maxWidth && !fullScreen
              ? {
                  maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
                  width: '100%',
                }
              : undefined,
        },
      }}
    >
      {header && (
        <DialogTitle className="modal-title">
          {header}
          <IconButton onClick={closeMessage} className="modal-close-button">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
      )}

      <DialogContent className={`modal-content ${fullScreen ? 'fullscreen' : ''}`}>{customBody}</DialogContent>

      {confirmLabel && (
        <DialogActions className="modal-actions">
          {cancelLabel && (
            <Box className="modal-cancel-button-wrapper">
              <Button size="medium" onClick={handleCancel}>
                {cancelLabel}
              </Button>
            </Box>
          )}
          <Button
            size="medium"
            onClick={handleConfirm}
            variantType={
              confirmButtonColor === 'error'
                ? 'error'
                : confirmButtonColor === 'warning'
                  ? 'warning'
                  : confirmButtonColor === 'success'
                    ? 'success'
                    : undefined
            }
            className="modal-confirm-button"
          >
            {confirmLabel}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ActionModal;
